import React, { useState, useEffect } from 'react';
import { profilesApi } from '../../api';
import HrnbModal from '../controls/HrnbModal';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbInput from '../controls/HrnbInput';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbSearchFilter from '../controls/HrnbSearchFilter';
import '../../css/duels/InviteModal.css';

const INVITE_TYPE_OPTIONS = [
    { value: 'PARTICIPANT', label: 'Участник' },
    { value: 'JUDGE', label: 'Судья' }
];

/**
 * Модальное окно отправки приглашения в дуэль
 */
export default function InviteModal({ open, onClose, duelId, onSuccess }) {
    const [profiles, setProfiles] = useState([]);
    const [filteredProfiles, setFilteredProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Форма
    const [selectedProfileId, setSelectedProfileId] = useState('');
    const [inviteType, setInviteType] = useState('PARTICIPANT');
    const [message, setMessage] = useState('');

    // Загрузка профилей
    useEffect(() => {
        console.log('[InviteModal] open changed:', open);
        if (open) {
            loadProfiles();
        }
    }, [open]);

    // Фильтрация профилей по поиску
    useEffect(() => {
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            setFilteredProfiles(
                profiles.filter(p =>
                    p.nickname?.toLowerCase().includes(query) ||
                    p.firstName?.toLowerCase().includes(query) ||
                    p.lastName?.toLowerCase().includes(query)
                )
            );
        } else {
            setFilteredProfiles(profiles);
        }
    }, [searchQuery, profiles]);

    const loadProfiles = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await profilesApi.list({ size: 1000 });
            // API v2 возвращает { content: [...], page: {...} }
            const profilesList = Array.isArray(response?.content)
                ? response.content
                : Array.isArray(response) ? response : [];
            setProfiles(profilesList);
            setFilteredProfiles(profilesList);
        } catch (err) {
            console.error('Ошибка при загрузке профилей:', err);
            setError('Не удалось загрузить список пользователей');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        setError(null);
        setSuccess(false);

        if (!selectedProfileId) {
            setError('Выберите пользователя');
            return;
        }

        if (!duelId) {
            setError('ID дуэли не указан');
            return;
        }

        setSubmitting(true);
        try {
            const { sendDuelInvite } = await import('../../api/duels');
            await sendDuelInvite(duelId, {
                inviteeProfileId: selectedProfileId,
                inviteType,
                message: message.trim() || undefined
            });

            setSuccess(true);
            setTimeout(() => {
                if (onSuccess) onSuccess();
                handleClose();
            }, 1500);
        } catch (err) {
            console.error('Ошибка при отправке приглашения:', err);
            setError(err.response?.data?.message || 'Не удалось отправить приглашение');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelectedProfileId('');
        setInviteType('PARTICIPANT');
        setMessage('');
        setSearchQuery('');
        setError(null);
        setSuccess(false);
        if (onClose) onClose();
    };

    // Опции для селекта (только отфильтрованные)
    const selectOptions = filteredProfiles.map(profile => ({
        value: profile.id,
        label: profile.nickname || profile.displayName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'Аноним'
    }));

    return (
        <HrnbModal
            open={open}
            onClose={handleClose}
            title="Пригласить участника"
        >
            <div className="invite-modal">
                {error && (
                    <HrnbAlert variant="danger" onClose={() => setError(null)}>
                        {error}
                    </HrnbAlert>
                )}

                {success && (
                    <HrnbAlert variant="success" onClose={() => setSuccess(false)}>
                        Приглашение успешно отправлено!
                    </HrnbAlert>
                )}

                {loading ? (
                    <HrnbLoader />
                ) : (
                    <div className="invite-modal__form">
                        <div className="invite-modal__search">
                            <HrnbSearchFilter
                                search={searchQuery}
                                onSearchChange={setSearchQuery}
                                onSearchSubmit={() => {}}
                                placeholder="Поиск по имени или никнейму..."
                                onClear={() => setSearchQuery('')}
                            />
                        </div>

                        <HrnbSelect
                            label="Пользователь *"
                            options={selectOptions}
                            value={selectedProfileId}
                            onChange={setSelectedProfileId}
                            placeholder="Выберите пользователя"
                            disabled={submitting}
                            required
                        />

                        <HrnbSelect
                            label="Роль *"
                            options={INVITE_TYPE_OPTIONS}
                            value={inviteType}
                            onChange={setInviteType}
                            placeholder="Выберите роль"
                            disabled={submitting}
                        />

                        <HrnbTextArea
                            label="Сообщение (необязательно)"
                            value={message}
                            onChange={(e) => setMessage(typeof e === 'string' ? e : e.target.value)}
                            placeholder="Например: Приглашаю в эпичную дуэль! 🔥"
                            rows={3}
                            maxLength={200}
                            disabled={submitting}
                        />

                        <div className="invite-modal__actions">
                            <HrnbButton
                                type="button"
                                variant="secondary"
                                onClick={handleClose}
                                disabled={submitting}
                            >
                                Отмена
                            </HrnbButton>
                            <HrnbButton
                                type="button"
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting || !selectedProfileId}
                            >
                                {submitting ? 'Отправка...' : 'Отправить приглашение'}
                            </HrnbButton>
                        </div>
                    </div>
                )}
            </div>
        </HrnbModal>
    );
}
